import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Alert, Row, Col, Card, Badge, Spinner, InputGroup } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import { API_BASE, IMAGE_BASE } from "../Config";
import axiosInstance from "../services/BaseService";

interface ContentData {
  _id: string;
  title: string;
  writer: string;
  content: string;
  imageLink?: string;
  videoLink?: string;
  contentType?: {
    _id: string;
    name: string;
    description: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ContentTypeData {
  _id: string;
  name: string;
  description: string;
}

const ManageAllContentPage: React.FC = () => {
  const [contents, setContents] = useState<ContentData[]>([]);
  const [contentTypes, setContentTypes] = useState<ContentTypeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingContent, setEditingContent] = useState<ContentData | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalContents, setTotalContents] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [writerQuery, setWriterQuery] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    writer: "",
    content: "",
    imageLink: "",
    videoLink: "",
    contentType: ""
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchContentsData(true);
    fetchContentTypes();
  }, []);

  const fetchContentsData = async (reset: boolean = false, searchTerm: string = '', writerTerm: string = '') => {
    try {
      if (reset) {
        setLoading(true);
        setPage(1);
      } else {
        setLoadingMore(true);
      }
      
      const currentPage = reset ? 1 : page;
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '12',
        ...(searchTerm && { search: searchTerm }),
        ...(writerTerm && { writer: writerTerm })
      });

      const response = await axiosInstance.get(`/contents?${params}`);
      const data = response.data;
      
      // Handle both old and new API response formats
      const contentsArray = data.contents || data || [];
      const totalCount = data.totalContents || contentsArray.length;
      const hasMoreData = data.hasMore !== undefined ? data.hasMore : false;
      
      if (reset) {
        setContents(contentsArray);
      } else {
        setContents(prev => [...prev, ...contentsArray]);
      }
      
      setHasMore(hasMoreData);
      setTotalContents(totalCount);
      setPage(currentPage + 1);
    } catch (error) {
      setError("Failed to fetch contents");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchContentTypes = async () => {
    try {
      const response = await axiosInstance.get('/contenttypes');
      setContentTypes(response.data);
    } catch (error) {
      setError("Failed to fetch content types");
    }
  };

  const handleSearch = () => {
    fetchContentsData(true, searchQuery, writerQuery);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchContentsData(false, searchQuery, writerQuery);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleShowModal = (content?: ContentData) => {
    if (content) {
      setEditingContent(content);
      setFormData({
        title: content.title || "",
        writer: content.writer || "",
        content: content.content || "",
        imageLink: content.imageLink || "",
        videoLink: content.videoLink || "",
        contentType: content.contentType?._id || ""
      });
    } else {
      setEditingContent(null);
      setFormData({
        title: "",
        writer: "",
        content: "",
        imageLink: "",
        videoLink: "",
        contentType: ""
      });
    }
    setSelectedFile(null);
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!selectedFile) return null;
    
    try {
      const { uploadImageToDb } = await import('../services/FileUploadService');
      const result = await uploadImageToDb(selectedFile, 'contentImage');
      return `/contentImage/${result.fileName}`;
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setUploading(true);
      
      let imageLink = formData.imageLink;
      if (selectedFile) {
        imageLink = (await uploadImage()) || '';
      }

      const submitData = { ...formData, imageLink };

      const url = editingContent ? `${API_BASE}/contents/${editingContent._id}` : `${API_BASE}/contents`;
      const method = editingContent ? 'PUT' : 'POST';

      const response = editingContent 
        ? await axiosInstance.put(`/contents/${editingContent._id}`, submitData)
        : await axiosInstance.post('/contents', submitData);

      if (response.status === 200 || response.status === 201) {
        setSuccess(editingContent ? "Content updated successfully" : "Content created successfully");
        await fetchContentsData(true, searchQuery, writerQuery);
        setShowModal(false);
      } else {
        throw new Error('Failed to save content');
      }
    } catch (error) {
      setError(editingContent ? "Failed to update content" : "Failed to create content");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (contentId: string) => {
    if (window.confirm("Are you sure you want to delete this content?")) {
      try {
        const response = await axiosInstance.delete(`/contents/${contentId}`);
        if (response.status === 200 || response.status === 204) {
          setSuccess("Content deleted successfully");
          await fetchContentsData(true, searchQuery, writerQuery);
        } else {
          throw new Error('Failed to delete content');
        }
      } catch (error) {
        setError("Failed to delete content");
      }
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <PermissionWrapper pageName="ManageAllContentPage">
      <div>
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <h3 className="mb-3 mb-md-0">
                <i className="bi bi-file-text me-2"></i>
                Manage All Content
              </h3>
              <Button variant="primary" onClick={() => handleShowModal()}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Content
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search by title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
              />
            </InputGroup>
          </Col>
          <Col md={4}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search by writer..."
                value={writerQuery}
                onChange={(e) => setWriterQuery(e.target.value)}
                onKeyPress={handleKeyPress}
              />
            </InputGroup>
          </Col>
          <Col md={2}>
            <Button variant="outline-secondary" onClick={handleSearch} className="w-100">
              <i className="bi bi-search me-1"></i>
              Search
            </Button>
          </Col>
          <Col md={2} className="d-flex align-items-center">
            <small className="text-muted">
              {contents.length > 0 ? `Showing ${contents.length} of ${totalContents} contents` : "No contents found"}
            </small>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        


        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            {contents.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-file-text display-1 text-muted"></i>
                <p className="text-muted mt-3">No contents found</p>
              </div>
            ) : (
              <Row className="g-3">
                {contents.map((content, index) => (
                  <Col xs={12} sm={6} md={4} lg={3} key={content._id || index}>
                    <Card className="h-100 content-card">
                      <div className="content-image-container">
                        {content.imageLink ? (
                          <Card.Img 
                            variant="top" 
                            src={IMAGE_BASE + content.imageLink}
                            className="content-image"
                            style={{ height: '200px', objectFit: 'cover' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/default-profile-image.jpg';
                            }}
                          />
                        ) : (
                          <div className="content-image-placeholder d-flex align-items-center justify-content-center" style={{ height: '200px', backgroundColor: '#f8f9fa' }}>
                            <i className="bi bi-file-text display-4 text-muted"></i>
                          </div>
                        )}
                      </div>
                      <Card.Body className="d-flex flex-column">
                        <Card.Title className="h6">{truncateText(content.title || 'Untitled', 50)}</Card.Title>
                        <Card.Text className="small text-muted mb-2">
                          <i className="bi bi-person me-1"></i>
                          {content.writer || 'Unknown Writer'}
                        </Card.Text>
                        <Card.Text className="small text-muted flex-grow-1">
                          {truncateText(content.content || 'No content', 100)}
                        </Card.Text>
                        <div className="content-info mb-2">
                          {content.contentType && (
                            <Badge bg="info" className="mb-1">
                              {content.contentType.name || 'Unknown Type'}
                            </Badge>
                          )}
                          {content.videoLink && (
                            <div className="small text-muted">
                              <i className="bi bi-play-circle me-1"></i>
                              Has Video
                            </div>
                          )}
                        </div>
                        <div className="d-flex gap-1 mt-auto">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowModal(content)}
                            className="flex-fill"
                          >
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(content._id)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}

        {/* Load More Button */}
        {hasMore && (
          <Row className="mt-4">
            <Col xs={12} className="text-center">
              <Button 
                variant="outline-primary" 
                size="lg" 
                onClick={handleLoadMore} 
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Loading...
                  </>
                ) : (
                  "Load More Contents"
                )}
              </Button>
            </Col>
          </Row>
        )}

        {/* Content Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingContent ? "Edit Content" : "Add Content"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Writer</Form.Label>
                    <Form.Control
                      type="text"
                      name="writer"
                      value={formData.writer}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Content Type</Form.Label>
                    <Form.Select
                      name="contentType"
                      value={formData.contentType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Content Type</option>
                      {contentTypes.map(type => (
                        <option key={type._id} value={type._id}>
                          {type.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Video Link (Optional)</Form.Label>
                    <Form.Control
                      type="url"
                      name="videoLink"
                      value={formData.videoLink}
                      onChange={handleInputChange}
                      placeholder="https://..."
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Content</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Content Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <Form.Text className="text-muted">
                  Upload an image for the content (JPG, PNG, GIF, SVG)
                </Form.Text>
                {formData.imageLink && (
                  <div className="mt-2">
                    <img 
                      src={IMAGE_BASE + formData.imageLink}
                      alt="Current content image"
                      style={{ width: '150px', height: '100px', objectFit: 'cover' }}
                      className="rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/default-profile-image.jpg';
                      }}
                    />
                  </div>
                )}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploading}>
                {uploading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  <>{editingContent ? "Update" : "Create"} Content</>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </PermissionWrapper>
  );
};

export default ManageAllContentPage;